import type { CampaignDto } from '@carbonwebmail/contracts'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { campaignHome } from './campaignLabels.js'

/**
 * The same campaigns, laid out by the day they went out.
 *
 * A table answers "what happened to this campaign". A calendar answers a
 * different question — "how often are we mailing these people, and when" —
 * which is the one that decides whether a list gets tired of you. Two views of
 * one list is the most the design allows and the least that answers both.
 *
 * **A draft has no place on it.** Nothing has happened to a draft and it has no
 * date to sit on; showing one against the day it was written would put a mark
 * on a calendar for a send that never occurred. Drafts stay on the table, and
 * this says how many are waiting rather than silently omitting them.
 *
 * **The dates are the viewer's own.** A send at 23:30 UTC lands on the next day
 * in Sydney, and a calendar that disagreed with the clock on the wall would be
 * wrong for everybody but us. The grid is built from local dates throughout.
 *
 * No data of its own: it renders what the table already fetched. A second query
 * for a second view of one list would be a second thing to keep in step.
 */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' })

export function CampaignCalendar({ campaigns }: { readonly campaigns: readonly CampaignDto[] }) {
  // The month containing the most recent send, so the calendar opens on
  // something rather than on an empty grid for whatever month it is today.
  const [month, setMonth] = useState(() => startOfMonth(latestSend(campaigns) ?? new Date()))

  const byDay = useMemo(() => groupByDay(campaigns), [campaigns])
  const drafts = campaigns.filter((campaign) => campaign.queuedAt === null).length

  return (
    <section>
      <header className="mb-4 flex items-center justify-between gap-4">
        <h2 className="font-bold text-[15px] text-t1">{MONTH.format(month)}</h2>
        <span className="flex gap-2">
          <Step label="Previous month" onClick={() => setMonth(shift(month, -1))}>
            ←
          </Step>
          <Step label="Next month" onClick={() => setMonth(shift(month, 1))}>
            →
          </Step>
        </span>
      </header>

      {drafts === 0 ? null : (
        <p className="mb-4 text-[13px] text-t2">
          {drafts === 1 ? '1 draft is' : `${drafts} drafts are`} not shown: a draft has not gone
          anywhere, so it has no day to sit on. They are on the table view.
        </p>
      )}

      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-card border border-line bg-line">
        {WEEKDAYS.map((day) => (
          <div key={day} className="bg-surface-2 px-2 py-2 font-semibold text-[12px] text-t2">
            {day}
          </div>
        ))}

        {cellsFor(month).map((day) => (
          <Day
            key={day.toISOString()}
            day={day}
            inMonth={day.getMonth() === month.getMonth()}
            campaigns={byDay.get(dayKey(day)) ?? []}
          />
        ))}
      </div>
    </section>
  )
}

function Day({
  day,
  inMonth,
  campaigns,
}: {
  readonly day: Date
  readonly inMonth: boolean
  readonly campaigns: readonly CampaignDto[]
}) {
  return (
    <div className={`min-h-[92px] p-2 ${inMonth ? 'bg-surface' : 'bg-surface-2'}`}>
      <span className={`text-[12px] ${inMonth ? 'text-t2' : 'text-t3'}`}>{day.getDate()}</span>
      <span className="mt-1 flex flex-col gap-1">
        {campaigns.map((campaign) => (
          <Link
            key={campaign.campaignId}
            to={campaignHome(campaign)}
            className="truncate rounded-field bg-brand-soft px-1.5 py-1 font-medium text-[12px] text-brand"
            title={campaign.name}
          >
            {campaign.name}
          </Link>
        ))}
      </span>
    </div>
  )
}

function Step({
  label,
  onClick,
  children,
}: {
  readonly label: string
  readonly onClick: () => void
  readonly children: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="rounded-field border border-line bg-surface px-3 py-1.5 text-[13px] text-t2"
    >
      {children}
    </button>
  )
}

/** Local dates throughout: a calendar has to agree with the clock on the wall. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function shift(month: Date, months: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + months, 1)
}

function latestSend(campaigns: readonly CampaignDto[]): Date | undefined {
  const sent = campaigns
    .map((campaign) => campaign.queuedAt)
    .filter((queuedAt): queuedAt is string => queuedAt !== null)
    .map((queuedAt) => new Date(queuedAt))

  if (sent.length === 0) return undefined
  return sent.reduce((latest, date) => (date > latest ? date : latest))
}

function groupByDay(campaigns: readonly CampaignDto[]): Map<string, CampaignDto[]> {
  const byDay = new Map<string, CampaignDto[]>()

  for (const campaign of campaigns) {
    // Drafts have no date. See the note above: a mark on a calendar for a send
    // that never happened is worse than an absence.
    if (campaign.queuedAt === null) continue

    const key = dayKey(new Date(campaign.queuedAt))
    byDay.set(key, [...(byDay.get(key) ?? []), campaign])
  }

  return byDay
}

/**
 * Six weeks from the Monday on or before the first of the month.
 *
 * Always six, so the grid does not change height as somebody steps through
 * months — a layout that jumps is one people stop trusting they have read.
 */
function cellsFor(month: Date): readonly Date[] {
  // getDay() is 0 for Sunday; this week starts on Monday, which is what a
  // British business calendar means by a week.
  const offset = (month.getDay() + 6) % 7
  const first = new Date(month.getFullYear(), month.getMonth(), 1 - offset)

  return Array.from(
    { length: 42 },
    (_unused, index) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + index),
  )
}
